import { useState } from 'react';

import InfoIcon from '@mui/icons-material/Info';
import {
  ClickAwayListener,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Popover,
  Typography,
  useTheme,
} from '@mui/material';

import usePrimaryNavStore from '@/components/shared/PrimaryNavDrawer/PrimaryNavStore';
import {
  NavItem,
  accessFilter,
  navItemHoverStyling,
  navItemTransition,
} from '@/components/shared/PrimaryNavDrawer/utils';
import { UserFeatureAccessType, useProductUpdatesQuery } from '@/generated';
import { useSession } from '@/providers/SessionProvider/SessionProvider';
import usePermissionAccess from '@/providers/UserPermissionsProvider/hooks/usePermissionAccess';
// TODO: feature_flag_cleanup: hasAccountPermissions - Remove this import when FF is fully rolled out
import useRoleAccess from '@/providers/UserPermissionsProvider/hooks/useRoleAccess';
import type { DeepReadonly } from '@/utils/globalTypes';

import NeedHelpForm from './NeedHelpForm/NeedHelpForm';

const DV_SUPPORT_URL = 'https://movableink.com/product-feedback';

const PrimaryNavSupport = () => {
  const { isDrawerOpen, isSupportSubMenuOpen, setIsSupportSubMenuOpen } =
    usePrimaryNavStore();
  const { primaryNav } = useTheme();
  const { currentUser, currentClient } = useSession();
  const { hasPermission } = usePermissionAccess();
  // TODO: feature_flag_cleanup: hasAccountPermissions - Remove useRoleAccess when FF is fully rolled out
  const { hasRole } = useRoleAccess();

  const hasAccountPermissions = currentClient?.hasAccountPermissions ?? false;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isNeedHelpDrawerOpen, setIsNeedHelpDrawerOpen] = useState(false);

  const { data: { productUpdates } = {} } = useProductUpdatesQuery();

  const latestProductUpdates = productUpdates?.[0];
  const productUpdatesTitle = (
    <>
      What's New?
      <Typography
        variant="caption"
        color="neutral500"
        sx={{
          ml: 0.5,
        }}
      >
        {latestProductUpdates?.title?.replace('Feature Updates:', '') ?? ''}
      </Typography>
    </>
  );

  const supportNavItems: DeepReadonly<NavItem[]> = [
    {
      id: 'whats-new',
      title: productUpdatesTitle,
      isStaff: false,
      featureAccess: UserFeatureAccessType.Release,
      domains: {
        prod:
          latestProductUpdates?.url ??
          'https://davinci.support.movableink.com/hc/en-us/categories/23862292741399-What-s-New-Feature-Releases',
      },
    },
    {
      id: 'need-help',
      title: 'Need Help?',
      domains: null,
      isStaff: false,
      featureAccess: UserFeatureAccessType.Release,
      action() {
        setIsSupportSubMenuOpen(false);
        setIsNeedHelpDrawerOpen(true);
      },
    },
    {
      id: 'support-center',
      title: 'Support Center',
      isStaff: false,
      featureAccess: UserFeatureAccessType.Release,
      domains: {
        prod: 'https://davinci-mi.zendesk.com/hc/en-us',
      },
    },
    {
      id: 'learning-hub',
      title: 'Learning Hub',
      isStaff: false,
      featureAccess: UserFeatureAccessType.Release,
      domains: {
        prod: 'https://learning.movableink.com/',
      },
    },
    {
      id: 'submit-product-feedback',
      title: 'Submit Product Feedback',
      domains: {
        prod: DV_SUPPORT_URL,
      },
      isStaff: false,
      featureAccess: UserFeatureAccessType.Release,
      action() {
        setIsSupportSubMenuOpen(false);
      },
    },
  ].filter((navItem) =>
    accessFilter({
      currentClient,
      currentUser,
      navItem,
      hasAccountPermissions,
      hasPermission,
      hasRole,
    }),
  );

  const supportId = isSupportSubMenuOpen ? 'support-items' : undefined;

  const handleSupportClick = (anchorEl: HTMLDivElement) => {
    setAnchorEl(anchorEl);
    setIsSupportSubMenuOpen(true);
  };

  const handleOnClose = () => {
    setIsNeedHelpDrawerOpen(false);
  };

  const getSupportItemAttributes = (url?: string) => {
    if (!url) {
      return {
        component: 'div',
      };
    }

    return {
      component: 'a',
      href: url,
      target: '_blank',
    };
  };

  return (
    <>
      <ListItemButton
        aria-describedby={supportId}
        sx={{
          px: 1.5,
          py: 0.75,
          '&:hover': navItemHoverStyling,
        }}
        onClick={(e) => handleSupportClick(e.currentTarget)}
        data-cy="primary-nav-support-btn"
      >
        <ListItemIcon
          sx={{
            minWidth: 40,
          }}
        >
          <InfoIcon sx={{ color: 'white' }} />
        </ListItemIcon>

        <Typography component="span" sx={navItemTransition(isDrawerOpen)}>
          Support
        </Typography>
      </ListItemButton>
      <Popover
        id={supportId}
        open={isSupportSubMenuOpen}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{ ml: 2 }}
      >
        <ClickAwayListener onClickAway={() => setIsSupportSubMenuOpen(false)}>
          <List
            sx={{
              bgcolor: primaryNav.backgroundColor,
              borderRadius: 1,
              color: 'white',
            }}
          >
            {supportNavItems.map(({ id, title, domains, action }) => (
              <ListItem
                key={id}
                sx={{
                  '&:hover': {
                    bgcolor: primaryNav.hoverBackgroundColor,
                  },
                }}
              >
                <ListItemButton
                  {...getSupportItemAttributes(domains?.prod)}
                  sx={{ p: 0 }}
                  onClick={() => action?.()}
                >
                  {title}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </ClickAwayListener>
      </Popover>
      <Drawer
        anchor="right"
        open={isNeedHelpDrawerOpen}
        onClose={handleOnClose}
        slotProps={{
          paper: {
            sx: { width: '320px' },
          },
        }}
      >
        <NeedHelpForm onClose={handleOnClose} />
      </Drawer>
    </>
  );
};

export default PrimaryNavSupport;
